/**
 * useHubPrefs — Feb 2027 · Shared hook for the founder's per-user
 * Hub preferences (`/users/me/hub-prefs`). Fetches once per session
 * via a module-level promise cache so multiple consumers don't fan
 * out into duplicate GETs, and rebroadcasts via a tiny event bus so
 * a Settings save propagates to every mounted consumer in real time.
 *
 * Returns `{ hubPrefs, loading }`. `hubPrefs` is null while loading
 * (so callers should fall back to their own defaults) and settles to
 * a snapshot of the resolved payload once the GET returns.
 *
 * Consumers who WRITE hub-prefs (currently only SettingsPage) should
 * call `refreshHubPrefs(freshData)` after their PATCH so every other
 * mounted hook picks up the change without a second network hit.
 */
import { useEffect, useState } from "react";
import { api } from "./apiClient";

const CACHE = { data: null, promise: null };
const LISTENERS = new Set();

function notify() {
  LISTENERS.forEach((cb) => {
    try {
      cb(CACHE.data);
    } catch {
      /* keep other listeners alive */
    }
  });
}

async function fetchHubPrefs() {
  if (CACHE.data) return CACHE.data;
  if (CACHE.promise) return CACHE.promise;
  CACHE.promise = api
    .get("/users/me/hub-prefs")
    .then((res) => {
      CACHE.data = res?.data || null;
      CACHE.promise = null;
      notify();
      return CACHE.data;
    })
    .catch(() => {
      CACHE.promise = null;
      // Leave CACHE.data null — callers apply their own defaults.
      return null;
    });
  return CACHE.promise;
}

export function refreshHubPrefs(data) {
  CACHE.data = data || null;
  CACHE.promise = null;
  notify();
}

export function useHubPrefs() {
  const [hubPrefs, setHubPrefs] = useState(CACHE.data);
  const [loading, setLoading] = useState(!CACHE.data);

  useEffect(() => {
    let alive = true;
    const listener = (fresh) => {
      if (alive) setHubPrefs(fresh);
    };
    LISTENERS.add(listener);
    if (!CACHE.data) {
      fetchHubPrefs().then((d) => {
        if (alive) {
          setHubPrefs(d);
          setLoading(false);
        }
      });
    } else {
      setLoading(false);
    }
    return () => {
      alive = false;
      LISTENERS.delete(listener);
    };
  }, []);

  return { hubPrefs, loading };
}
